import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Users, Plus, Link2, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { useSecurity } from "../context/SecurityContext";
import { fetchMembers, addMember, removeMember, updateMember } from "../lib/api";
import { MemberRow, ResetPinForm, AddMemberForm, MemberEmailForm } from "./FamilyMemberForms";
import { InviteForm, InviteResult, PendingInvites } from "./InviteMember";
import { MakeMeAdminCard } from "./MakeMeAdminCard";
import { Button } from "../components/ui/button";

const detail = (e, fallback) => e?.response?.data?.detail || fallback;

export const FamilyMembersSection = () => {
  const qc = useQueryClient();
  const { member: me, isOwner, isAdmin, adminAccess, refreshStatus } = useSecurity();
  const canAppointAdmin = isAdmin || adminAccess?.reason === "bootstrap";
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [invite, setInvite] = useState(null);

  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: fetchMembers });

  const refresh = async () => {
    qc.invalidateQueries({ queryKey: ["members"] });
    qc.invalidateQueries({ queryKey: ["activity"] });
    await refreshStatus();
  };

  const run = async (fn, successMsg, fallback) => {
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await refresh();
      return true;
    } catch (e) {
      toast.error(detail(e, fallback));
      return false;
    }
  };

  const handleAdd = async (payload) => {
    setBusy(true);
    const ok = await run(() => addMember(payload), `${payload.name} can now unlock the vault with their own PIN`, "Could not add member");
    setBusy(false);
    if (ok) {
      setAdding(false);
      if (payload.role === "executor" && !payload.email) {
        const res = await fetchMembers().catch(() => null); const list = res?.members || (Array.isArray(res) ? res : []);
        const created = list.find((x) => x.name === payload.name && x.role === "executor" && !x.email);
        if (created) { setEmailing(created); toast.message(`Add ${payload.name}'s email so recovery-kit copies and access requests reach them`); }
      }
    }
  };

  const handleRemove = (m) => {
    if (!window.confirm(`Remove ${m.name}'s access to the vault?`)) return;
    run(() => removeMember(m.id), `${m.name} removed`, "Could not remove member");
  };

  const handleRole = async (m, role) => {
    const ok = await run(() => updateMember(m.id, { role }), `${m.name} is now ${role}`, "Could not change role");
    if (ok && role === "executor" && !m.email) { setEmailing({ ...m, role }); toast.message(`Add ${m.name}'s email — executors get recovery-kit copies and access-request alerts`); }
  };
  const handleLang = (m, lang) => run(() => updateMember(m.id, { lang }), `${m.name} will get emails and texts in ${lang === "es" ? "Spanish" : "English"}`, "Could not change language");

  const [emailing, setEmailing] = useState(null);
  const handleEmail = async ({ name, email }) => {
    const ok = await run(() => updateMember(emailing.id, { name, email }), "Saved", "Could not save");
    if (ok) {
      setEmailing(null);
      qc.invalidateQueries({ queryKey: ["weekly-digest-settings"] });
      if (me?.id === emailing.id) refreshStatus();
    }
  };

  const handleResetPin = async (pin) => {
    const ok = await run(() => updateMember(resetting.id, { pin }), "PIN reset", "Could not reset PIN");
    if (ok) setResetting(null);
  };

  return (
    <div className="mt-6" data-testid="family-members-section">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" /> Family members
        </h4>
        {isOwner && !adding && !inviting && (
          <div className="flex gap-1.5">
            <Button data-testid="invite-member-toggle-btn" size="sm" variant="outline" onClick={() => setInviting(true)} className="gap-1.5 h-8">
              <Link2 className="h-3.5 w-3.5" /> Invite
            </Button>
            <Button data-testid="add-member-toggle-btn" size="sm" variant="outline" onClick={() => setAdding(true)} className="gap-1.5 h-8">
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          </div>
        )}
      </div>

      {isOwner && members.some((m) => !m.email) && (
        <div className="rounded-xl border border-amber-300/70 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3 text-sm flex items-start gap-3" data-testid="members-email-nudge">
          <MailWarning className="h-4 w-4 text-amber-700 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-amber-900 dark:text-amber-200">{members.filter((m) => !m.email).map((m) => m.name).join(", ")} {members.filter((m) => !m.email).length === 1 ? "has" : "have"} no email on file</div>
            <div className="text-xs text-amber-800/80 dark:text-amber-300/80 mt-0.5">{members.some((m) => !m.email && m.role === "executor")
              ? <span data-testid="members-email-nudge-executor"><b>{members.filter((m) => !m.email && m.role === "executor").map((m) => m.name).join(", ")} is your executor</b> — without an email the quarterly recovery-kit copy and executor access requests can't reach them.</span>
              : "Without one they'd miss letter-opening notes, invites and emergency alerts."}</div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {members.filter((m) => !m.email).map((m) => (
                <button key={m.id} type="button" onClick={() => setEmailing(m)} data-testid={`members-email-nudge-${m.id}`} className="rounded-full border border-amber-400/70 bg-white/70 dark:bg-transparent px-2.5 py-1 text-xs text-amber-900 dark:text-amber-200 hover:border-amber-600">Add {m.name}'s email</button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {members.map((m) => (
          <MemberRow key={m.id} m={m} isMe={me?.id === m.id} isOwner={isOwner} canAppointAdmin={canAppointAdmin} canTouch={m.role !== "admin" || isAdmin} onRole={handleRole} onResetPin={setResetting} onRemove={handleRemove} onEmail={setEmailing} onLang={handleLang} />
        ))}
      </div>

      {isOwner && <MakeMeAdminCard />}
      {emailing && <MemberEmailForm key={emailing.id} target={emailing} onSave={handleEmail} onCancel={() => setEmailing(null)} />}
      {resetting && <ResetPinForm target={resetting} onSave={handleResetPin} onCancel={() => setResetting(null)} />}
      {isOwner && adding && <AddMemberForm onSave={handleAdd} onCancel={() => setAdding(false)} busy={busy} canAppointAdmin={canAppointAdmin} />}
      {isOwner && inviting && !invite && (
        <InviteForm
          onCreated={(inv) => {
            setInvite(inv);
            qc.setQueryData(["invites"], (old = []) => [inv, ...old]);
            qc.invalidateQueries({ queryKey: ["invites"] });
          }}
          onCancel={() => setInviting(false)}
        />
      )}
      {invite && (
        <InviteResult
          invite={invite}
          onDone={() => {
            setInvite(null);
            setInviting(false);
          }}
        />
      )}
      {isOwner && <PendingInvites />}
      {!isOwner && <p className="mt-2 text-[11px] text-muted-foreground">Only the vault owner can add or remove members.</p>}
    </div>
  );
};
